<div class="revisions">
  @for (revision of revisions(); track $index; let i = $index) {
    <div class="revision-card"
         [class.active-revision]="i === activeIndex()"
         [class.collapsed]="!isExpanded(i)">

      <!-- Header — clickable for past revisions -->
      <div class="revision-header" tabindex="0" (click)="toggleRevision(i)" (keydown.enter)="toggleRevision(i)" [class.clickable]="i !== activeIndex()">
        @if (i !== activeIndex() && !isExpanded(i)) {
          <span class="expand-icon">&#9654;</span>
        } @else if (i !== activeIndex()) {
          <span class="expand-icon">&#9660;</span>
        }

        @if (revision.endDate) {
          <span class="date-range">
            from {{ revision.startDate | date:'dd.MM.yyyy' }} to {{ revision.endDate | date:'dd.MM.yyyy' }}
          </span>
        } @else {
          <span class="date-range">
            Starting from {{ revision.startDate | date:'dd.MM.yyyy' }}
          </span>
        }

        <span class="inline-product">{{ revision.productName }}</span>
      </div>

      <!-- Body — only shown when expanded -->
      @if (isExpanded(i)) {
        <div class="revision-body">
          <!-- Two-column layout: left = product/extensions, right = individual pricing -->
          <div class="revision-columns">
            <div class="revision-col-main">
              <div class="field-row">
                <span class="field-label"><em>Product:</em></span>
                <span class="field-value">
                  <strong>{{ revision.productName }}: {{ revision.productCount }} x {{ planPrice(revision) | number:'1.2-2' }} {{ currency() }}</strong>
                  @if (revision.discount) {
                    <span class="discount-badge">{{ formatDiscount(revision.discount) }}</span>
                  }
                </span>
              </div>

              <div class="field-row">
                <span class="field-label"><em>Price list:</em></span>
                <span class="field-value"><strong>{{ revision.priceListName }}</strong></span>
              </div>

              @if (revision.bookedExtensions.length > 0) {
                <div class="field-row">
                  <span class="field-label"><em>Extensions:</em></span>
                  <span class="field-value extensions-list">
                    <!-- Same naming convention as the Individual Pricing column beside this one
                         (`:75-78`): the technical name always shows in parentheses, the readable
                         name is prefixed when the localization map has one. Extensions were the
                         only place that hid the technical name entirely, which is why a price
                         package without a readable name rendered as a price with no label at all.
                         Tracked by the technical name — the stable identity — not by the labelKey,
                         which is a display key and can be empty (PR #42 review #8). -->
                    @for (ext of sortedExtensions(revision); track ext.extensionPriceInfo.name) {
                      <span class="extension-item">
                        <strong>
                          @if (extensionLabel(ext)) { {{ extensionLabel(ext) }} }
                          ({{ ext.extensionPriceInfo.name }}): {{ ext.count }} x {{ ext.effectivePrice | number:'1.2-2' }} {{ currency() }}
                        </strong>
                        @if (ext.discount) {
                          <span class="discount-badge">{{ formatDiscount(ext.discount) }}</span>
                        }
                      </span>
                    }
                  </span>
                </div>
              }
            </div>

            @if (revision.bookedFeatures.length > 0) {
              <div class="revision-col-pricing">
                <div class="section-label"><em>Individual Pricing:</em></div>
                <!-- Fixed initial height with a one-time "Show all" (EB-4006, same
                     pattern as Reachabilities); the extensions column stays full. -->
                <cmt-capped-list [count]="revision.bookedFeatures.length" [threshold]="8">
                  @for (feature of revision.bookedFeatures; track feature.name) {
                    <div class="feature-block">
                      <div class="feature-line-1">
                        @if (getFeatureDisplayName(feature.name)) {
                          <strong>{{ getFeatureDisplayName(feature.name) }}</strong>
                        }
                        ({{ feature.name }}):
                        <!-- Deliberately `> 0`, unlike the plan/extension badges: old CMT shows no
                             badge for features at all (revisionBlock.html:46 prints `discount * unitPrice`),
                             so there is no parity target for a negative feature discount. -->
                        @if (feature.discount > 0) {
                          {{ formatDiscount(feature.discount) }} &times;
                        }
                        {{ feature.unitPrice | number:'1.2-2' }} {{ currency() }}
                      </div>
                      <div class="feature-line-2">
                        @if (feature.contingent !== null) {
                          (contingent: {{ feature.contingent }})
                        }
                        {{ feature.activeDays }}
                        @if (feature.activeFrom) { from {{ feature.activeFrom }} }
                        @if (feature.activeUntil) { until {{ feature.activeUntil }} }
                      </div>
                    </div>
                  }
                </cmt-capped-list>
              </div>
            }
          </div>

          <!-- Created/Changed by -->
          <div class="meta-row">
            <span class="meta-info"><em>Created by:</em></span>
            @if (revision.creatingEmployeeID !== null) {
              <span class="meta-info">{{ revision.creatingEmployeeName || 'Employee #' + revision.creatingEmployeeID }}</span>
            } @else {
              <span class="meta-info">automatic process</span>
            }
            @if (revision.creationDate) {
              <span class="meta-info">({{ revision.creationDate | date:'dd.MM.yyyy' }})</span>
            }
            @if (revision.changedBy !== null) {
              <span class="meta-info meta-separator">| <em>Changed by:</em></span>
              <span class="meta-info">{{ revision.changedByName || 'Employee #' + revision.changedBy }}</span>
              @if (revision.changedOn) {
                <span class="meta-info">({{ revision.changedOn | date:'dd.MM.yyyy' }})</span>
              }
            }
          </div>

          <!-- Reason (operator justification, e.g. a permanent discount) -->
          @if (revision.reason) {
            <div class="reason-row">
              <span class="field-label"><em>Reason:</em></span>
              <span class="reason-text">{{ revision.reason }}</span>
            </div>
          }
        </div>
      }
    </div>
  }
</div>
